import { useCallback, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";
import { defaultRangeExtractor, useVirtualizer } from "@tanstack/react-virtual";

type Props<T extends { id: string }> = {
  items: readonly T[];
  scrollElement: HTMLDivElement | null;
  headerRef?: RefObject<HTMLDivElement | null>;
  estimateSize: number;
  revealPrependAtStart?: boolean;
  children(item: T, index: number): ReactNode;
};

/** Presentation only: callers retain their data, disclosures and scroll container. */
export function   VirtualHistoryRows<T extends { id: string }>({ items, scrollElement, headerRef, estimateSize, revealPrependAtStart = false, children }: Props<T>) {
  const [headerHeight, setHeaderHeight] = useState(0);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const previousItems = useRef(items);
  // Read the still-committed scroll position before TanStack handles this update.
  // Require all old IDs in order, allowing new IDs between them or at the end.
  // Replacements and same-ID updates
  // keep the existing anchoring policy. Trade opts in; Mint remains unchanged.
  const previousFirst = previousItems.current[0];
  const retainedStart = revealPrependAtStart && scrollElement !== null && scrollElement.scrollTop <= 1 &&
    items !== previousItems.current && items.length > previousItems.current.length && previousFirst ?
    items.findIndex(item => item.id === previousFirst.id) : -1;
  let revealPrepend = false;
  if (retainedStart > 0) {
    let previousIndex = 0;
    for (let index = retainedStart; index < items.length && previousIndex < previousItems.current.length; index++) {
      if (items[index]!.id === previousItems.current[previousIndex]!.id) previousIndex++;
    }
    revealPrepend = previousIndex === previousItems.current.length;
  }
  const getItemKey = useCallback((index: number) => items[index]!.id, [items]);
  const focusedIndex = useMemo(() => focusedId === null ? -1 : items.findIndex(item => item.id === focusedId), [focusedId, items]);
  const rangeExtractor = useCallback((range: Parameters<typeof defaultRangeExtractor>[0]) => {
    const indexes = defaultRangeExtractor(range);
    // Retain the actual focused DOM node. Its neighbours preserve native Tab order,
    // even after the user scrolls the focused row far outside the rendered window.
    if (focusedIndex >= 0) {
      for (let index = Math.max(0, focusedIndex - 1); index <= Math.min(items.length - 1, focusedIndex + 1); index++) indexes.push(index);
    }
    return [...new Set(indexes)].sort((a, b) => a - b);
  }, [focusedIndex, items.length]);
  const virtualizer = useVirtualizer<HTMLDivElement, HTMLDivElement>({
    count: items.length,
    getScrollElement: () => scrollElement,
    getItemKey,
    estimateSize: () => estimateSize,
    overscan: 4,
    rangeExtractor,
    scrollMargin: headerHeight,
    scrollPaddingStart: headerHeight,
    // The library preserves the visible record on prepends using getItemKey.
    // Disable end pinning as well as append following: expanding the last row
    // must not pull the reader to the bottom of its details.
    anchorTo: revealPrepend ? "start" : "end",
    followOnAppend: false,
    scrollEndThreshold: -1,
    // Read on remount as well as resize. Cached offscreen sizes may belong to
    // another viewport width or an earlier disclosure state.
    measureElement: element => element.getBoundingClientRect().height,
  });

  useLayoutEffect(() => { previousItems.current = items; }, [items]);

  useLayoutEffect(() => {
    const header = headerRef?.current;
    if (!header) return;
    const measure = () => setHeaderHeight(header.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    return () => observer.disconnect();
  }, [headerRef]);

  const focusedRow = (target: EventTarget | null) => {
    if (!(target instanceof Element) || !rootRef.current?.contains(target)) return null;
    return target.closest<HTMLElement>("[data-virtual-history-row]")?.dataset.virtualHistoryRow ?? null;
  };

  return <div ref={rootRef} style={{ height: virtualizer.getTotalSize(), position: "relative", overflowAnchor: "none" }}
    onFocusCapture={event => setFocusedId(focusedRow(event.target))}
    onBlurCapture={event => setFocusedId(focusedRow(event.relatedTarget))}>
    {virtualizer.getVirtualItems().map(row => <div key={row.key} data-index={row.index} data-virtual-history-row={items[row.index]!.id}
      ref={virtualizer.measureElement}
      style={{ position: "absolute", top: 0, left: 0, width: "100%", transform: `translateY(${row.start - headerHeight}px)` }}>
      {children(items[row.index]!, row.index)}
    </div>)}
  </div>;
}
